import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  PlayIcon,
  ArrowRightIcon,
  SparklesIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const LandingPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const openImageModal = (image, title) => {
    setSelectedImage({ image, title });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  // Animations variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const scaleIn = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
  };

  const features = [
    {
      icon: <CalendarIcon className="h-12 w-12 text-primary" />,
      title: "Gestion du calendrier",
      description: "Planifiez et organisez tous vos événements, répétitions et spectacles en un seul endroit. Visualisez facilement les disponibilités et évitez les conflits de programmation.",
      image: "/images/calendrier.png"
    },
    {
      icon: <UserGroupIcon className="h-12 w-12 text-primary" />,
      title: "Coordination des équipes",
      description: "Gérez efficacement vos équipes techniques et artistiques. Assignez les rôles, suivez les disponibilités et coordonnez les interventions.",
      image: "/images/GestionUser.png"
    },
    {
      icon: <BuildingOfficeIcon className="h-12 w-12 text-primary" />,
      title: "Gestion des salles",
      description: "Optimisez l'utilisation de vos espaces. Suivez l'occupation des salles, gérez les réservations et planifiez les aménagements.",
      image: "/images/dashboard.png"
    },
    {
      icon: <DocumentTextIcon className="h-12 w-12 text-primary" />,
      title: "Documents centralisés",
      description: "Stockez et partagez tous vos documents importants : contrats, factures, bilans, déclarations. Accès sécurisé pour toute l'équipe.",
      image: "/images/GestionPdf.png"
    }
  ];

  const trustFeatures = [
    {
      icon: <ShieldCheckIcon className="h-8 w-8 text-green-600" />,
      title: "Sécurité & Données",
      points: [
        "Chiffrement des données sensibles",
        "Sauvegarde automatique quotidienne",
        "Accès sécurisé par rôles",
        "Conformité RGPD"
      ]
    },
    {
      icon: <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-600" />,
      title: "Support",
      points: [
        "Assistance technique dédiée",
        "Formation à l'utilisation",
        "Documentation complète",
        "Support par email"
      ]
    },
    {
      icon: <ArrowPathIcon className="h-8 w-8 text-purple-600" />,
      title: "Mises à jour",
      points: [
        "Nouvelles fonctionnalités régulières",
        "Corrections automatiques",
        "Amélioration continue",
        "Feedback utilisateurs intégré"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <motion.section 
        className="relative overflow-hidden bg-gradient-to-r from-primary to-secondary py-20 px-4"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div 
            className="flex justify-center mb-8"
            variants={scaleIn}
            whileHover={{ scale: 1.05 }}
          >
            <img 
              src="/logo.png" 
              alt="Comédie des Suds" 
              className="h-24 drop-shadow-lg"
            />
          </motion.div>
          
          <motion.h1 
            className="text-5xl md:text-6xl font-bold text-white mb-6"
            variants={fadeInUp}
          >
            Planning-Corporate
            <motion.span 
              className="block text-gold text-3xl md:text-4xl mt-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              Gestion d'événements culturels
            </motion.span>
          </motion.h1>
          
          <motion.p 
            className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            La solution complète pour optimiser la gestion de vos événements, 
            coordonner vos équipes et maximiser l'utilisation de vos espaces culturels.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            variants={fadeInUp}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="#features" 
                className="inline-flex items-center px-8 py-4 bg-gold text-primary font-semibold rounded-lg shadow-lg hover:bg-yellow-400 transition-colors"
              >
                Découvrir
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/login" 
                className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary transition-colors"
              >
                Se connecter
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Features Section */}
      <motion.section 
        id="features"
        className="py-20 px-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" variants={fadeInUp}>
            <h2 className="text-4xl font-bold text-primary mb-4">
              Fonctionnalités principales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez comment Planning-Corporate révolutionne la gestion 
              de vos événements culturels
            </p>
          </motion.div>

          <div className="space-y-20">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className={`flex flex-col lg:flex-row items-center gap-12 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                variants={fadeInUp}
              >
                <div className="flex-1">
                  <motion.div 
                    className="flex items-center mb-6"
                    whileHover={{ x: 5 }}
                  >
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      {feature.icon}
                    </motion.div>
                    <h3 className="text-3xl font-bold text-primary ml-4">
                      {feature.title}
                    </h3>
                  </motion.div>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    {feature.description}
                  </p>
                  <motion.div 
                    className="inline-flex items-center text-primary font-semibold"
                    whileHover={{ x: 5 }}
                  >
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Voir en action
                  </motion.div>
                </div>
                
                <motion.div 
                  className="flex-1"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-4 shadow-lg">
                    <div className="bg-white rounded-lg p-2 overflow-hidden">
                      <img 
                        src={feature.image} 
                        alt={feature.title}
                        className="w-full h-auto rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                        onClick={() => openImageModal(feature.image, feature.title)}
                      />
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Trust Section */}
      <motion.section 
        className="py-20 px-4 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" variants={fadeInUp}>
            <h2 className="text-4xl font-bold text-primary mb-4">
              Une solution de confiance
            </h2>
            <p className="text-xl text-gray-600">
              Sécurité, support et évolution continue
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={staggerContainer}
          >
            {trustFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-lg p-8 shadow-lg"
                variants={fadeInUp}
                whileHover={{ 
                  y: -5, 
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)" 
                }}
              >
                <motion.div 
                  className="flex items-center mb-6"
                  whileHover={{ scale: 1.05 }}
                >
                  {feature.icon}
                  <h3 className="text-xl font-bold text-gray-800 ml-3">
                    {feature.title}
                  </h3>
                </motion.div>
                <ul className="space-y-3">
                  {feature.points.map((point, pointIndex) => (
                    <motion.li 
                      key={pointIndex}
                      className="flex items-start"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * pointIndex }}
                    >
                      <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{point}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Pricing Section */}
      <motion.section 
        className="py-20 px-4 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" variants={fadeInUp}>
            <h2 className="text-4xl font-bold text-primary mb-4">
              Nos formules d'abonnement
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choisissez la formule qui correspond à vos besoins et à la taille de votre organisation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Basic Plan */}
            <motion.div 
              className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-shadow"
              variants={fadeInUp}
              whileHover={{ y: -5 }}
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Basic</h3>
                <p className="text-gray-600 mb-4">Jusqu'à 50 utilisateurs</p>
                <div className="text-3xl font-bold text-primary mb-2">Sur devis</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Calendrier événements</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Gestion des équipes</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Exports Excel</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Support email</span>
                </li>
              </ul>
              <motion.button 
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Choisir Basic
              </motion.button>
            </motion.div>

            {/* Professional Plan */}
            <motion.div 
              className="bg-white rounded-xl shadow-lg p-8 border-2 border-gold relative hover:shadow-xl transition-shadow"
              variants={fadeInUp}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gold text-primary px-4 py-1 rounded-full text-sm font-semibold">
                  Recommandé
                </span>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Professionnel</h3>
                <p className="text-gray-600 mb-4">Jusqu'à 100 utilisateurs</p>
                <div className="text-3xl font-bold text-primary mb-2">Sur devis</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Toutes les fonctionnalités Basic</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Gestion des salles</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Archives événements</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Rôles personnalisés</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Support prioritaire</span>
                </li>
              </ul>
              <motion.button 
                className="w-full bg-gold text-primary py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Choisir Professionnel
              </motion.button>
            </motion.div>

            {/* Enterprise Plan */}
            <motion.div 
              className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-shadow"
              variants={fadeInUp}
              whileHover={{ y: -5 }}
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Entreprise</h3>
                <p className="text-gray-600 mb-4">Jusqu'à 250 utilisateurs</p>
                <div className="text-3xl font-bold text-primary mb-2">Sur devis</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Toutes les fonctionnalités Pro</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Personnalisation avancée</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>API dédiée</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Formation sur site</span>
                </li>
                <li className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Support téléphonique</span>
                </li>
              </ul>
              <motion.button 
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Choisir Entreprise
              </motion.button>
            </motion.div>
          </div>

          <motion.div 
            className="text-center mt-12"
            variants={fadeInUp}
          >
            <p className="text-lg text-gray-600 mb-4">
              Besoin de plus de 250 utilisateurs ?
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center px-8 py-4 bg-primary text-white font-semibold rounded-lg shadow-lg hover:bg-primary-dark transition-colors"
            >
              Nous consulter
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Footer */}
      <motion.footer 
        className="bg-gray-900 text-white py-12 px-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div variants={fadeInUp}>
              <img 
                src="/logo.png" 
                alt="Comédie des Suds" 
                className="h-16 mb-4"
              />
              <p className="text-gray-400">
                Planning-Corporate - Solution de gestion d'événements culturels
              </p>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-gray-400">
                <p>520b avenue du Col de l'Ange</p>
                <p>13420 Gémenos</p>
                <p>SIRET: 432896173000 64</p>
              </div>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <h4 className="text-lg font-semibold mb-4">Liens rapides</h4>
              <div className="space-y-2">
                <Link to="/login" className="block text-gray-400 hover:text-white transition-colors">
                  Connexion
                </Link>
                <Link to="/register" className="block text-gray-400 hover:text-white transition-colors">
                  Inscription
                </Link>
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400"
            variants={fadeInUp}
          >
            <p>© {new Date().getFullYear()} Comédie des Suds. Tous droits réservés.</p>
          </motion.div>
        </div>
      </motion.footer>

      {/* Modal pour agrandir les images */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 text-white text-2xl font-bold bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
            >
              ×
            </button>
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-4 right-4 text-center">
              <p className="text-white text-lg font-semibold bg-black bg-opacity-50 rounded-lg px-4 py-2">
                {selectedImage.title}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;